import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_CONFIG, hasIncompleteLegalConfig } from '../config/legal';
import './LegalPage.css';

export default function PrivacyPage() {
  const {
    productName,
    operatorName,
    contactEmail,
    mailingAddress,
    effectiveDate,
  } = LEGAL_CONFIG;

  return (
    <main className="legal-page">
      <div className="legal-page__inner">
        <Link className="legal-page__brand" to="/">{productName}</Link>
        <header className="legal-page__header">
          <h1>Privacy Policy</h1>
          <p className="legal-page__meta">Effective: {effectiveDate}</p>
          <p className="legal-page__intro">
            This Privacy Policy explains how {operatorName}, the operator of {productName}, collects, uses, stores, and discloses information when you use the service.
          </p>
        </header>

        {hasIncompleteLegalConfig && (
          <aside className="legal-page__draft-warning" role="note">
            Draft notice: replace the bracketed operator, contact, address, and state information before public launch and have qualified privacy counsel review this document.
          </aside>
        )}

        <section>
          <h2>1. Scope and Eligibility</h2>
          <p>This Policy applies to the {productName} website and web application. The service is intended for users in the United States who are at least 13 years old.</p>
          <p>{productName} is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided information, contact us so we can investigate and delete it.</p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>Account and authentication information</h3>
          <p>When you create or use an account, we may receive your email address, display name, authentication-provider identifier, user identifier, account creation and sign-in metadata, and profile information made available through Google Sign-In. Firebase Authentication processes credentials. {productName} does not store your plain-text password.</p>

          <h3>Student and planning information</h3>
          <p>You choose whether to enter information such as:</p>
          <ul>
            <li>High school, graduation year, city, state, intended major, and other profile details.</li>
            <li>GPA, target GPA, SAT, ACT, PSAT, AP/IB courses, dual-enrollment courses, activities, and awards.</li>
            <li>College research lists, categories, custom fields, notes, imported spreadsheet values, and saved links.</li>
            <li>Final college lists, application types, deadlines, submitted status, tasks, reminders, calendar entries, and progress records.</li>
            <li>Settings, theme preferences, notifications, and onboarding state.</li>
          </ul>

          <h3>Technical and service information</h3>
          <p>Our hosting, authentication, database, and security providers may automatically process information such as IP address, browser and device type, timestamps, request information, authentication events, and diagnostic or security logs. We also use browser storage and IndexedDB to cache account data, preferences, and the public college dataset on your device.</p>
        </section>

        <section>
          <h2>3. How We Use Information</h2>
          <p>We use information to:</p>
          <ul>
            <li>Create and authenticate accounts.</li>
            <li>Provide dashboards, college lists, progress tracking, calendars, reminders, customization, imports, and exports.</li>
            <li>Synchronize planning information across devices signed into the same account.</li>
            <li>Cache information locally for faster loading and limited resilience when network service is slow.</li>
            <li>Maintain, troubleshoot, secure, and improve the service.</li>
            <li>Respond to support, privacy, and legal requests.</li>
            <li>Comply with law and protect users, the service, and others.</li>
          </ul>
          <p>We do not use student planning information to make admissions decisions or submit applications on a user’s behalf.</p>
        </section>

        <section>
          <h2>4. Where Information Is Stored</h2>
          <p>Account planning data is stored in Google Cloud Firestore under the signed-in user’s account identifier. Firebase Authentication handles sign-in records. Copies are cached in browser storage on devices used to sign in so the app can load faster.</p>
          <p>Signing out disconnects the app from the account but may not erase that account’s local cache. Clearing browser storage removes local copies from that browser. Clearing browser data does not necessarily delete information stored with the account in Firestore.</p>
        </section>

        <section>
          <h2>5. When We Disclose Information</h2>
          <p>We do not sell personal information. We do not currently use student planning information for targeted advertising or share it for cross-context behavioral advertising.</p>
          <p>We may disclose information in these circumstances:</p>
          <ul>
            <li><strong>Service providers:</strong> Google and Firebase process information for authentication, hosting, database storage, synchronization, and related infrastructure.</li>
            <li><strong>At your direction:</strong> when you choose to export information, open a saved third-party link, or otherwise direct information outside {productName}.</li>
            <li><strong>Legal and safety reasons:</strong> when reasonably necessary to comply with law, legal process, or valid government requests; enforce agreements; investigate abuse; or protect rights, safety, and security.</li>
            <li><strong>Business changes:</strong> in connection with a proposed or completed financing, merger, acquisition, reorganization, or sale, subject to appropriate confidentiality and notice requirements.</li>
          </ul>
        </section>

        <section>
          <h2>6. College Data and External Services</h2>
          <p>{productName} downloads public college information from the U.S. Department of Education College Scorecard API and caches that dataset on the device. College Scorecard and college websites are separate services with their own privacy practices.</p>
          <p>Links saved by users may lead to third-party services such as college portals, document providers, or application platforms. This Policy does not govern those services.</p>
        </section>

        <section>
          <h2>7. Retention and Deletion</h2>
          <p>We generally retain account and planning information while the account remains active or as reasonably needed to provide the service. We may retain limited records longer when necessary for security, fraud prevention, legal compliance, dispute resolution, or enforcing agreements.</p>
          <p>To request deletion of your account and associated Firestore planning data, contact <a href={`mailto:${contactEmail}`}>{contactEmail}</a> from the email connected to the account. We may need to verify the request. We will act within a reasonable period and as required by applicable law. Service-provider backups or security records may remain for a limited period before deletion or de-identification.</p>
          <p>Local cached information may remain on a device after account deletion until browser storage is cleared. On a shared device, clear site data after signing out if you do not want a local account cache to remain.</p>
        </section>

        <section>
          <h2>8. Your Choices and Rights</h2>
          <p>Depending on where you live, you may have rights to request access to, correction of, deletion of, or a copy of personal information, and to appeal certain decisions. You may update much of your planning information directly in the service.</p>
          <p>To exercise a privacy right, email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>. We will verify and respond to requests as required by applicable law. We will not discriminate against you for exercising a privacy right.</p>
          <p>Because we do not currently sell personal information or use it for cross-context behavioral advertising, we do not offer a sale or targeted-advertising opt-out. If our practices change, we will update this Policy and provide required choices.</p>
        </section>

        <section>
          <h2>9. Cookies, Browser Storage, and Do Not Track</h2>
          <p>The service uses browser storage, IndexedDB, and technologies used by Firebase to maintain authentication, synchronize account information, remember preferences, cache data, and support security. Disabling browser storage may prevent parts of the service from working.</p>
          <p>{productName} does not currently track users across unrelated websites for advertising. Because there is no uniform standard for browser “Do Not Track” signals, the service does not separately respond to those signals. We will honor legally recognized opt-out signals if they become applicable to our practices.</p>
        </section>

        <section>
          <h2>10. Security</h2>
          <p>We use reasonable administrative and technical safeguards designed to protect information, including Firebase Authentication, account-scoped storage, Firestore security rules, encrypted network connections, and access controls. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.</p>
          <p>You can help by using a unique password, protecting access to your email and Google account, signing out on shared devices, and avoiding storage of highly sensitive information that {productName} does not request.</p>
        </section>

        <section>
          <h2>11. Users Under 18</h2>
          <p>Users aged 13 through 17 should review this Policy with a parent or legal guardian. A parent or guardian may contact us about a minor’s account, but we may need to verify authority and the account relationship before disclosing or deleting information.</p>
        </section>

        <section>
          <h2>12. United States Processing</h2>
          <p>The service is offered in the United States. Information may be processed and stored in the United States or other locations where our service providers operate, subject to their contractual and legal safeguards.</p>
        </section>

        <section>
          <h2>13. Changes to This Policy</h2>
          <p>We may update this Policy to reflect changes in the service, providers, or law. We will post the revised Policy with a new effective date and provide additional notice when required or when changes materially affect users.</p>
        </section>

        <section className="legal-page__contact">
          <h2>14. Contact Us</h2>
          <p><strong>Operator:</strong> {operatorName}</p>
          <p><strong>Email:</strong> <a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
          <p><strong>Mailing address:</strong> {mailingAddress}</p>
        </section>
      </div>
    </main>
  );
}
